// .fmapply（Web → Unity）シリアライザ（SPEC_FMAPPLY.md）。
// manifest.json / clips.json / assign.json を組み立てて zip 化する。

import { zipSync, strToU8 } from "fflate";
import {
  DEFAULT_APPLY_OPTIONS,
  DEFAULT_TRACKING,
  FMAPPLY_FORMAT_VERSION,
  TOOL_VERSION,
  optionsOf,
  shapeKeyOf,
  trackingOf,
  type ApplyOptions,
  type FaceClip,
  type FmAvatarManifest,
  type ProjectState,
  type TrackingSetting,
} from "./types.ts";
import { fail } from "./errors.ts";

export { DEFAULT_APPLY_OPTIONS, DEFAULT_TRACKING, type ApplyOptions, type TrackingSetting };

const WHERE = ".fmapply の書き出し";

/** SPEC_FMAPPLY §3.2: クリップ名の禁止文字 */
// eslint-disable-next-line no-control-regex
const FORBIDDEN_NAME_CHARS = /[\\/:*?"<>|]/;

export interface FmApplyInput {
  avatarManifest: Pick<FmAvatarManifest, "avatarName" | "avatarUid">;
  project: ProjectState;
  options?: Partial<ApplyOptions>;
  /** テスト用に固定できる書き出し時刻 */
  exportedAt?: string;
}

export interface FmApplyJson {
  manifest: Record<string, unknown>;
  clips: Record<string, unknown>;
  assign: Record<string, unknown>;
}

/** クリップ配列を検証する（SPEC_FMAPPLY §6 の Web 側で担保できる分）。 */
export function validateClips(clips: FaceClip[]): void {
  const seenIndex = new Set<number>();
  const seenName = new Set<string>();
  for (const c of clips) {
    if (!Number.isInteger(c.index) || c.index < 0) {
      fail(WHERE, `クリップ "${c.name}" の index が非負整数ではありません。`);
    }
    if (seenIndex.has(c.index)) fail(WHERE, `クリップ index=${c.index} が重複しています。`);
    seenIndex.add(c.index);

    if (c.name.length === 0) fail(WHERE, "名前が空のクリップがあります。", "クリップ名を入力してください。");
    if (c.name.length > 255) fail(WHERE, `クリップ名 "${c.name.slice(0, 20)}…" が 255 文字を超えています。`);
    if (FORBIDDEN_NAME_CHARS.test(c.name)) {
      fail(WHERE, `クリップ名 "${c.name}" に使用できない文字が含まれています。`, `\\ / : * ? " < > | は使えません。`);
    }
    if (seenName.has(c.name)) {
      fail(WHERE, `クリップ名 "${c.name}" が重複しています。`, "クリップ名は大文字小文字を区別して一意である必要があります。");
    }
    seenName.add(c.name);

    if (!Number.isFinite(c.durationSeconds) || c.durationSeconds < 0) {
      fail(WHERE, `クリップ "${c.name}" の遷移時間が 0 以上の数値ではありません。`);
    }

    if (c.tracking) {
      if (c.tracking.eyes !== "animation" && c.tracking.eyes !== "tracking") {
        fail(WHERE, `クリップ "${c.name}" のまばたき設定(${c.tracking.eyes})が不正です。`);
      }
      if (c.tracking.mouth !== "expression" && c.tracking.mouth !== "lipsync") {
        fail(WHERE, `クリップ "${c.name}" の口の設定(${c.tracking.mouth})が不正です。`);
      }
    }

    validateShapeList(c.name, c.shapes);

    if (c.type === "keyframe") {
      const kfs = c.keyframes ?? [];
      if (kfs.length < 2) {
        fail(WHERE, `キーフレームクリップ "${c.name}" にはキーフレームが 2 枚以上必要です。`, "キーフレームを追加するか、静的クリップに戻してください。");
      }
      let prev = -1;
      for (const kf of kfs) {
        if (!Number.isFinite(kf.time) || kf.time < 0) {
          fail(WHERE, `クリップ "${c.name}" のキーフレーム時刻(${kf.time})が 0 以上の数値ではありません。`);
        }
        if (kf.time <= prev) {
          fail(WHERE, `クリップ "${c.name}" のキーフレーム時刻が昇順ではありません（${prev} → ${kf.time}）。`);
        }
        prev = kf.time;
        validateShapeList(`${c.name} @${kf.time}s`, kf.shapes);
      }
    } else if (c.type !== "static") {
      fail(WHERE, `クリップ "${c.name}" の type(${String(c.type)})が不正です。`);
    }
  }
}

function validateShapeList(label: string, shapes: FaceClip["shapes"]): void {
  const seenShape = new Set<string>();
  for (const s of shapes) {
    const key = shapeKeyOf(s.meshIndex, s.name);
    if (seenShape.has(key)) {
      fail(WHERE, `クリップ "${label}" 内でシェイプキー "${s.name}" が重複しています。`);
    }
    seenShape.add(key);
    if (!Number.isFinite(s.value) || s.value < -1000 || s.value > 1000) {
      fail(WHERE, `クリップ "${label}" のシェイプ "${s.name}" の値(${s.value})が -1000〜1000 の範囲外です。`);
    }
  }
}

/** メニュー/トグル表情を assign.json の menu[] へ（SPEC_FMAPPLY §4.6）。 */
function menuEntries(project: ProjectState, clipsByIndex: Map<number, FaceClip>): Record<string, unknown>[] {
  const menu = project.menu ?? [];
  if (menu.length > 255) fail(WHERE, `メニュー表情が ${menu.length} 件あります。255 件以下にしてください。`);
  const out: Record<string, unknown>[] = [];
  const seen = new Set<number>();
  for (const m of menu) {
    if (!Number.isInteger(m.index) || m.index < 1) {
      fail(WHERE, `メニュー "${m.label}" の index(${m.index})が 1 以上の整数ではありません。`);
    }
    if (seen.has(m.index)) fail(WHERE, `メニューの index=${m.index} が重複しています。`);
    seen.add(m.index);
    if (!m.label || m.label.length > 255) {
      fail(WHERE, `メニューの表示名が不正です（1〜255 文字）: "${m.label}"`);
    }
    let clip: FaceClip | null = null;
    if (m.clipIndex !== null && m.clipIndex !== undefined) {
      clip = clipsByIndex.get(m.clipIndex) ?? null;
      if (!clip) {
        fail(WHERE, `メニュー "${m.label}" が存在しないクリップ(index=${m.clipIndex})を参照しています。`);
      }
    }
    const entry: Record<string, unknown> = {
      index: m.index,
      label: m.label,
      clipRef: clip ? clip.index : null,
      tracking: trackingOf(clip),
    };
    if (m.iconGuid) entry["iconGuid"] = m.iconGuid;
    out.push(entry);
  }
  return out;
}

function validateOptions(o: ApplyOptions): void {
  if (!/^[A-Za-z0-9_]{1,16}$/.test(o.parameterPrefix)) {
    fail(WHERE, `パラメータ接頭辞 "${o.parameterPrefix}" が不正です。`, "英数字とアンダースコアのみ、1〜16 文字で指定してください。");
  }
}

/** 割り当て配列（8 スロット）を assign.json の形へ。未割り当ては出力しない（既存表情を維持）。 */
function gestureEntries(slots: (number | null)[], clipsByIndex: Map<number, FaceClip>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  for (let g = 0; g < slots.length; g++) {
    const clipRef = slots[g];
    if (clipRef === null || clipRef === undefined) continue;
    const clip = clipsByIndex.get(clipRef);
    if (!clip) {
      fail(WHERE, `ジェスチャー ${g} が存在しないクリップ(index=${clipRef})を参照しています。`);
    }
    out.push({ gesture: g, clipRef, tracking: trackingOf(clip) });
  }
  return out;
}

/**
 * 左右コンビネーションを assign.json の combos[] へ（SPEC_FMAPPLY §4.4）。
 * (left, right) の重複は不可。clipIndex が null のセルは出力しない。
 */
function comboEntries(combos: ProjectState["combos"], clipsByIndex: Map<number, FaceClip>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  for (const c of combos ?? []) {
    if (!Number.isInteger(c.left) || c.left < 0 || c.left > 7 ||
        !Number.isInteger(c.right) || c.right < 0 || c.right > 7) {
      fail(WHERE, `コンビネーション (${c.left}, ${c.right}) のジェスチャー番号が 0〜7 の範囲外です。`);
    }
    const key = `${c.left},${c.right}`;
    if (seen.has(key)) {
      fail(WHERE, `コンビネーション (左${c.left}, 右${c.right}) が重複しています。`);
    }
    seen.add(key);
    if (c.clipIndex === null || c.clipIndex === undefined) continue;
    const clip = clipsByIndex.get(c.clipIndex);
    if (!clip) {
      fail(WHERE, `コンビネーション (左${c.left}, 右${c.right}) が存在しないクリップ(index=${c.clipIndex})を参照しています。`);
    }
    out.push({ left: c.left, right: c.right, clipRef: c.clipIndex, tracking: trackingOf(clip) });
  }
  return out;
}

/** 値 0 のシェイプは省略する（未記載=0、SPEC_FMAPPLY §3.2）。 */
function shapesJson(shapes: FaceClip["shapes"]): Record<string, unknown>[] {
  return shapes
    .filter((s) => s.value !== 0)
    .map((s) => ({ meshIndex: s.meshIndex, name: s.name, value: s.value }));
}

/** clips.json の 1 要素へ。keyframe クリップは keyframes[] を出す（SPEC_FMAPPLY §2.3）。 */
function serializeClip(c: FaceClip): Record<string, unknown> {
  const base: Record<string, unknown> = {
    index: c.index,
    name: c.name,
    type: c.type === "keyframe" ? "keyframe" : "static",
    durationSeconds: c.durationSeconds,
    assetGuid: c.assetGuid ?? null,
  };
  if (c.type === "keyframe") {
    base["loop"] = c.loop === true;
    base["keyframes"] = (c.keyframes ?? []).map((kf) => ({ time: kf.time, shapes: shapesJson(kf.shapes) }));
    return base;
  }
  base["shapes"] = shapesJson(c.shapes);
  return base;
}

/** .fmapply の 3 つの JSON をオブジェクトとして構築する（zip 化前）。 */
export function buildFmApplyJson(input: FmApplyInput): FmApplyJson {
  const options: ApplyOptions = { ...optionsOf(input.project), ...(input.options ?? {}) };
  validateOptions(options);

  const clips = input.project.clips;
  validateClips(clips);
  const clipsByIndex = new Map(clips.map((c) => [c.index, c]));

  const manifest: Record<string, unknown> = {
    formatVersion: FMAPPLY_FORMAT_VERSION,
    avatarName: input.avatarManifest.avatarName,
    exportedAt: input.exportedAt ?? new Date().toISOString(),
    toolVersion: TOOL_VERSION,
  };
  if (input.avatarManifest.avatarUid) {
    manifest["avatarUid"] = input.avatarManifest.avatarUid;
  }

  const clipsJson: Record<string, unknown> = {
    formatVersion: FMAPPLY_FORMAT_VERSION,
    clips: clips.map((c) => serializeClip(c)),
  };

  const assignJson: Record<string, unknown> = {
    formatVersion: FMAPPLY_FORMAT_VERSION,
    applyMode: input.project.applyMode,
    options: {
      parameterPrefix: options.parameterPrefix,
      priorityHand: options.priorityHand,
      suppressOtherHandOnPriority: options.suppressOtherHandOnPriority,
      writeDefaults: options.writeDefaults,
      menuInstallPath: options.menuInstallPath,
      menuLabel: options.menuLabel,
      overrideExistingGestureLayers: options.overrideExistingGestureLayers,
      exportAllClipsAsAnim: options.exportAllClipsAsAnim,
    },
    gestures: {
      left: gestureEntries(input.project.gestures.left, clipsByIndex),
      right: gestureEntries(input.project.gestures.right, clipsByIndex),
      combos: comboEntries(input.project.combos, clipsByIndex),
    },
    menu: menuEntries(input.project, clipsByIndex),
  };

  return { manifest, clips: clipsJson, assign: assignJson };
}

/** .fmapply zip のバイト列を生成する。 */
export function serializeFmApply(input: FmApplyInput): Uint8Array {
  const json = buildFmApplyJson(input);
  return zipSync(
    {
      "manifest.json": strToU8(JSON.stringify(json.manifest, null, 2)),
      "clips.json": strToU8(JSON.stringify(json.clips, null, 2)),
      "assign.json": strToU8(JSON.stringify(json.assign, null, 2)),
    },
    { level: 6 },
  );
}

/** ダウンロード用のファイル名を作る（禁止文字を除去）。 */
export function fmApplyFileName(avatarName: string): string {
  const safe = avatarName.replace(/[\\/:*?"<>|]/g, "_").trim() || "avatar";
  return `${safe}.fmapply`;
}
